import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';

const Footer = () => {
  // Functions to handle navigation using correct base URL and hash format
  const handleNavigation = (path) => (e) => {
    e.preventDefault();
    window.location.href = `https://munger.ai/#${path}`;
  };
  
  // Function to handle smooth scroll to pricing section
  const scrollToPricing = (e) => {
    e.preventDefault();
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth' });
    } else {
      // If we're not on the home page, go to home page + pricing
      window.location.href = '/#pricing';
    }
  };

  return (
    <footer className="relative bg-gradient-to-b from-gray-950 to-black py-16 overflow-hidden">
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-purple-900/10 via-transparent to-transparent" />
      
      {/* Subtle animated glow */}
      <motion.div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-gradient-to-t from-purple-500/10 to-transparent blur-3xl"
        animate={{
          opacity: [0.3, 0.5, 0.3],
        }}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut"
        }}
      />
      
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="mb-6 md:mb-0"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500/20 to-blue-500/20 backdrop-blur-sm border border-purple-500/30">
                <Sparkles className="w-5 h-5 text-purple-400" />
              </div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Munger
              </h3>
            </div>
            <p className="text-gray-400 text-sm max-w-xs">
              AI-First email automation for business owners & builders
            </p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-gray-400"
          >
            <ul className="flex flex-col md:flex-row space-y-2 md:space-y-0 md:space-x-6 text-sm items-center">
              <li>
                <a 
                  href="#features" 
                  className="hover:text-white hover:text-shadow-glow transition-all duration-300"
                >
                  Features
                </a>
              </li>
              <li>
                <a 
                  href="#pricing" 
                  onClick={scrollToPricing} 
                  className="hover:text-white hover:text-shadow-glow transition-all duration-300"
                >
                  Pricing
                </a>
              </li>
              <li>
                <a 
                  href="#" 
                  className="hover:text-white hover:text-shadow-glow transition-all duration-300"
                >
                  Contact
                </a>
              </li>
              <li>
                <button 
                  onClick={handleNavigation('/login')}
                  className="font-medium text-gray-400 hover:text-white transition-all duration-300"
                >
                  Sign In
                </button>
              </li>
              <li>
                <button 
                  onClick={handleNavigation('/sign-up')}
                  className="group relative inline-flex h-10 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-6 text-sm font-medium text-white shadow-lg transition-all duration-300 hover:shadow-purple-500/25 hover:scale-105"
                >
                  <span className="relative z-10">Sign Up Free</span>
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-70" />
                </button>
              </li>
            </ul>
          </motion.div>
        </div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-12 pt-6 border-t border-gray-800/50 text-center"
        >
          <p className="text-gray-500 text-sm">
            &copy; {new Date().getFullYear()} Munger. All rights reserved.
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
